import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../../hooks/useTelegram';

const Card = styled.div`
  cursor: pointer;
  transition: transform 0.15s;
  &:active { transform: scale(0.97); }
`;

const Cover = styled.div`
  width: 100%;
  aspect-ratio: ${p => p.$ratio || '3/4'};
  border-radius: 14px;
  overflow: hidden;
  background: #1a1a2e;
  position: relative;
`;

const CoverImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${p => p.$loaded ? 1 : 0};
  transition: opacity 0.3s;
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a1a2e, #0a0a0f);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.15);
  font-size: 28px;
`;

const Discount = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ff3b30;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
  z-index: 2;
`;

const BadgeRow = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: flex;
  gap: 4px;
  z-index: 2;
  flex-wrap: wrap;
`;

const Badge = styled.div`
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 5px;
`;

const LangBadge = styled(Badge)`
  background: rgba(0,111,255,0.7);
`;

const Title = styled.div`
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Prices = styled.div`
  margin-top: 5px;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const PriceFrom = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$sale ? '#FFD700' : '#fff'};
`;

const PriceLabel = styled.span`
  font-size: 11px;
  color: rgba(255,255,255,0.35);
`;

const OldP = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: rgba(255,255,255,0.6);
  text-decoration: line-through;
`;

function getMinRegionPrice(prices) {
  let min = Infinity, minOld = null;
  for (const r of ['tr', 'ua', 'in']) {
    const p = prices?.[r];
    if (p?.price_rub && p.price_rub < min) {
      min = p.price_rub;
      minOld = p.old_price_rub || null;
    }
  }
  return { price: min === Infinity ? 0 : min, old: minOld };
}

function getMaxDiscount(prices) {
  let maxDisc = 0;
  for (const r of ['tr', 'ua', 'in']) {
    const p = prices?.[r];
    if (p?.old_price_rub && p.price_rub) {
      const d = Math.round(((p.old_price_rub - p.price_rub) / p.old_price_rub) * 100);
      if (d > maxDisc) maxDisc = d;
    }
  }
  return maxDisc;
}

const GameCard = ({ game, ratio }) => {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const [loaded, setLoaded] = useState(false);
  if (!game) return null;

  const { price, old } = getMinRegionPrice(game.prices);
  const disc = getMaxDiscount(game.prices);
  const platforms = game.platform?.includes('/') ? game.platform.split('/') : [game.platform];

  return (
    <Card onClick={() => { hapticFeedback?.('light'); navigate(`/game/${encodeURIComponent(game.id || game.sony_id)}`); }}>
      <Cover $ratio={ratio}>
        {!loaded && <Placeholder>🎮</Placeholder>}
        {game.image_url && (
          <CoverImg
            src={game.image_url}
            alt={game.title}
            $loaded={loaded}
            onLoad={() => setLoaded(true)}
            onError={(e) => { e.target.style.display = 'none'; }}
            loading="lazy"
          />
        )}
        {disc > 0 && <Discount>−{disc}%</Discount>}
        <BadgeRow>
          {platforms.map(p => <Badge key={p}>{p?.trim()}</Badge>)}
          {game.rus_voice && <LangBadge>🎙 РУС</LangBadge>}
        </BadgeRow>
      </Cover>
      <Title>{game.title}</Title>
      <Prices>
        <PriceLabel>от </PriceLabel>
        <PriceFrom $sale={!!old}>{price === 0 ? 'Бесплатно' : `${price.toLocaleString('ru')} ₽`}</PriceFrom>
        {old && <OldP>{old.toLocaleString('ru')} ₽</OldP>}
      </Prices>
    </Card>
  );
};

export default GameCard;
